/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  Switch,
  useTheme,
  alpha
} from '@mui/material';
import { useParams, useHistory } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { CONFIRMATION_MSG, USER_ROLES } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import {
  getFormattedDate,
  getFuelQuantity,
  isAdminUser
} from 'src/utils/getInitials';
import shouldDisplayDetailField from 'src/utils/detailFieldUtils';
import { getCardDetails, updateCardDetails } from 'src/redux/cardSlice';
import useAuth from 'src/context/AuthContext';
import { APIService } from 'src/api/api';

import NewCard from './NewCard';
import { getFormattedCardBody } from './formModel/cardFormUtils';
import CardSyncStatusModal from './CardSyncStatusModal';

/* -------------------- Helpers -------------------- */

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const isISODate = (value) => {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return typeof value === 'string' && isoRegex.test(value);
};

const formatValue = (key, value, configuredValue) => {
  if (value === null || value === undefined) return '--';

  // Quantity formatting
  if (key === 'limit_quantity') {
    return getFuelQuantity(value, configuredValue);
  }

  // Boolean formatting
  if (typeof value === 'boolean') {
    return value ? 'ON' : 'OFF';
  }

  // ISO Date formatting
  if (isISODate(value)) {
    return getFormattedDate(value);
  }

  return value;
};

const shouldRenderField = (key, value) => {
  if (!shouldDisplayDetailField(key)) return false;

  // Remove nested objects & arrays
  if (typeof value === 'object' && value !== null) return false;

  // Exclude internal relation fields
  const excludedFields = [
    'admin',
    'site',
    'created_by',
    'updated_by',
    'card_dispenser_mapper',
    'dispensers'
  ];
  if (excludedFields.includes(key)) return false;

  return true;
};

const PRIORITY_CARD_DETAIL_KEYS = ['uid', 'card_category', 'card_type'];

/* -------------------- Component -------------------- */

function CardDetails() {
  const theme = useTheme();
  const { cardId } = useParams();
  const history = useHistory();
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = isAdminUser(getUser);
  const isSupervisor = user?.role === USER_ROLES.siteManager;

  const dispatch = useDispatch();
  const { loading, cardDetails } = useSelector((state) => state.cards);

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCardSyncModal, setShowCardSyncModal] = useState(false);

  const filteredCardFields = Object.entries(cardDetails || {})
    .filter(([key, value]) => shouldRenderField(key, value));

  const orderedCardFields = [
    ...PRIORITY_CARD_DETAIL_KEYS
      .map((key) => filteredCardFields.find(([fieldKey]) => fieldKey === key))
      .filter(Boolean),
    ...filteredCardFields.filter(
      ([fieldKey]) => !PRIORITY_CARD_DETAIL_KEYS.includes(fieldKey)
    )
  ];

  useEffect(() => {
    dispatch(getCardDetails(cardId));
  }, [dispatch, cardId]);

  /* -------------------- Status Toggle -------------------- */

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const cardBody = getFormattedCardBody(cardDetails);
      const values = { ...cardBody, active: !cardBody.active };

      await dispatch(updateCardDetails(values)).unwrap();

      dispatch(
        setSuccessInfo({ successMsg: 'Card status updated successfully!' })
      );
    } catch (error) {
      dispatch(
        setErrorInfo({
          errorMsg:
            error?.response?.data?.error_message
            || error?.message
            || 'Failed to update card status'
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------- Remove Dispenser -------------------- */

  const handleRemoveDispenser = async (mapperId) => {
    try {
      await APIService.removeDispenserFromCard(mapperId);

      dispatch(
        setSuccessInfo({
          successMsg: 'The dispenser has been unlinked from the card'
        })
      );

      await dispatch(getCardDetails(cardId)).unwrap();
    } catch (error) {
      console.error('Unlink Error:', error);

      dispatch(
        setErrorInfo({
          errorMsg:
            error?.response?.data?.error_message
            || error?.message
            || 'Failed to unlink dispenser from the card'
        })
      );
    }
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NewCard
        open={openModal}
        initialValues={cardDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)',
        }}
      >
        {/* HEADER */}
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                CARD DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage card configuration
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.cards)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Back
            </Button>
          )}
        />

        {/* CONTENT */}
        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {orderedCardFields.map(([key, value]) => {
              if (key === 'active') {
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.02)
                        }
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          STATUS
                        </Typography>
                        <Typography
                          fontWeight={600}
                          color={value ? 'success.main' : 'error.main'}
                        >
                          {value ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>

                      <Switch
                        checked={value}
                        onChange={() => (isAdmin || isSupervisor)
                          && setShowConfirmModal(true)}
                        disabled={!isAdmin && !isSupervisor}
                        color="success"
                      />
                    </Box>
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5
                      }}
                    >
                      {formatLabel(key)}
                    </Typography>

                    <Typography variant="body1" fontWeight={600}>
                      {formatValue(key, value, cardDetails?.[`${key}_gallons`])}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>

        {/* ACTIONS */}
        {(isAdmin || isSupervisor) && (
          <>
            <Divider />
            <CardActions sx={{
              p: 2.5, justifyContent: 'flex-end', gap: 2, position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
            }}
            >
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Edit Card Details
              </Button>

              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={() => setShowCardSyncModal(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Card Sync Status
              </Button>
            </CardActions>
          </>
        )}
      </Card>

      <ConfirmationDialog
        info={
          cardDetails?.active
            ? CONFIRMATION_MSG.disableCard
            : CONFIRMATION_MSG.enableCard
        }
        open={showConfirmModal}
        disable={cardDetails?.active}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />

      <CardSyncStatusModal
        open={showCardSyncModal}
        onClose={() => setShowCardSyncModal(false)}
        data={cardDetails}
        onRemoveDispenser={handleRemoveDispenser}
      />
    </Container>
  );
}

export default CardDetails;
